import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users, Search, Filter, Download, ChevronRight, MapPin,
  Calendar, DollarSign, Star, UserCheck, UserX, Eye
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const SEGMENT_CONFIG = {
  vip: { label: 'VIP', color: '#fac775', bg: 'rgba(250,199,117,0.12)', border: 'rgba(250,199,117,0.25)' },
  regulier: { label: 'Régulier', color: '#00E01A', bg: 'rgba(0,224,26,0.1)', border: 'rgba(0,224,26,0.25)' },
  decouverte: { label: 'Découverte', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)' },
  inactif: { label: 'Inactif', color: '#8C8F94', bg: 'rgba(140,143,148,0.1)', border: 'rgba(140,143,148,0.25)' },
};

function formatCurrency(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

export default function ExploitationClients() {
  const { api } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState(searchParams.get('segment') || '');
  const [siteFilter, setSiteFilter] = useState(searchParams.get('site') || '');
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (segmentFilter) params.append('segment', segmentFilter);
      if (siteFilter) params.append('site', siteFilter);
      const [clientsRes, sitesRes] = await Promise.all([
        api.get(`/exploitation/clients?${params.toString()}`),
        api.get('/exploitation/sites'),
      ]);
      setClients(clientsRes.data);
      setSites(sitesRes.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, [api, search, segmentFilter, siteFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get('/exploitation/clients/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'clients_exploitation.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export CSV téléchargé');
    } catch {
      toast.error("Erreur lors de l'export");
    } finally { setExporting(false); }
  };

  // Segment stats
  const segmentCounts = clients.reduce((acc, c) => {
    acc[c.segment] = (acc[c.segment] || 0) + 1;
    return acc;
  }, {});

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[50vh]"><div className="w-8 h-8 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exploitation-clients">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            Clients Exploitation
          </h1>
          <p className="text-[#8C8F94] text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>
            {clients.length} client{clients.length > 1 ? 's' : ''} trouvé{clients.length > 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={exporting}
          className="flex items-center gap-2 bg-[#1D2327] hover:bg-[#2C3338] text-white border border-[#2C3338] px-4 py-2.5 rounded-full text-sm transition-all disabled:opacity-50"
          data-testid="export-csv-btn"
        >
          <Download size={16} /> Export CSV
        </button>
      </div>

      {/* Segment Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="segment-stats">
        {Object.entries(SEGMENT_CONFIG).map(([key, cfg]) => (
          <button
            key={key}
            onClick={() => setSegmentFilter(segmentFilter === key ? '' : key)}
            className={`rounded-xl p-4 text-left transition-all border ${
              segmentFilter === key
                ? 'ring-1 ring-offset-0'
                : 'hover:scale-[1.02]'
            }`}
            style={{
              backgroundColor: cfg.bg,
              borderColor: segmentFilter === key ? cfg.color : cfg.border,
              ringColor: cfg.color,
            }}
            data-testid={`segment-filter-${key}`}
          >
            <p className="text-2xl font-bold" style={{ color: cfg.color, fontFamily: "'Bebas Neue', sans-serif" }}>
              {segmentCounts[key] || 0}
            </p>
            <p className="text-xs uppercase tracking-wider mt-0.5" style={{ color: cfg.color, fontFamily: "'Barlow Condensed', sans-serif", opacity: 0.8 }}>
              {cfg.label}
            </p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52555A]" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher un client..."
            className="pl-9 bg-[#111111] border-[#222] text-white placeholder:text-[#52555A]"
            data-testid="client-search"
          />
        </div>
        <select
          value={siteFilter}
          onChange={e => setSiteFilter(e.target.value)}
          className="bg-[#111111] border border-[#222] text-white rounded-lg px-3 py-2 text-sm focus:border-[#00E01A] outline-none"
          data-testid="site-filter"
        >
          <option value="">Tous les sites</option>
          {sites.map(s => (
            <option key={s.site_id} value={s.site_id}>{s.name}</option>
          ))}
        </select>
        {(segmentFilter || siteFilter || search) && (
          <button
            onClick={() => { setSearch(''); setSegmentFilter(''); setSiteFilter(''); }}
            className="text-[#8C8F94] hover:text-white text-xs px-3 py-2 rounded-lg bg-[#1D2327] hover:bg-[#2C3338] transition-colors"
            data-testid="clear-filters"
          >
            Effacer les filtres
          </button>
        )}
      </div>

      {/* Client Grid */}
      {clients.length === 0 ? (
        <div className="bg-[#111111] rounded-xl border border-[#222] p-12 text-center">
          <Users size={40} className="mx-auto text-[#52555A] mb-3" />
          <p className="text-[#8C8F94]">Aucun client trouvé</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="clients-grid">
          {clients.map(client => {
            const seg = SEGMENT_CONFIG[client.segment] || SEGMENT_CONFIG.inactif;
            return (
              <Link
                key={client.id}
                to={`/exploitation/clients/${client.id}`}
                className="bg-[#111111] rounded-xl border border-[#222] p-4 hover:border-[#00E01A]/30 transition-all group"
                data-testid={`client-card-${client.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: seg.bg, color: seg.color, border: `1px solid ${seg.border}` }}>
                      {client.first_name?.[0]}{client.last_name?.[0]}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold text-sm group-hover:text-[#00E01A] transition-colors">
                        {client.first_name} {client.last_name}
                      </h3>
                      <p className="text-[#52555A] text-xs">{client.email || 'Pas d\'email'}</p>
                    </div>
                  </div>
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                    style={{ backgroundColor: seg.bg, color: seg.color, border: `1px solid ${seg.border}` }}
                  >
                    {seg.label}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bg-[#0a0a0a] rounded-lg p-2.5 text-center">
                    <p className="text-white font-bold text-sm" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      {formatCurrency(client.total_ca)}
                    </p>
                    <p className="text-[#52555A] text-[9px] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>CA</p>
                  </div>
                  <div className="bg-[#0a0a0a] rounded-lg p-2.5 text-center">
                    <p className="text-white font-bold text-sm" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      {client.total_sessions}
                    </p>
                    <p className="text-[#52555A] text-[9px] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Sessions</p>
                  </div>
                  <div className="bg-[#0a0a0a] rounded-lg p-2.5 text-center">
                    <p className="text-white font-bold text-sm" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      {client.sites?.length || 0}
                    </p>
                    <p className="text-[#52555A] text-[9px] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Sites</p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#52555A]">
                    {client.last_activity
                      ? `Dernière activité : ${new Date(client.last_activity).toLocaleDateString('fr-FR')}`
                      : 'Aucune activité'}
                  </span>
                  <ChevronRight size={14} className="text-[#52555A] group-hover:text-[#00E01A] transition-colors" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
